import React, { PropsWithChildren, useEffect, useCallback } from "react";
import { Log as OidcLog, User, WebStorageStateStore } from "oidc-client-ts";
import {
  AuthProviderProps,
  AuthProvider as OidcAuthProvider,
  useAuth,
} from "react-oidc-context";
import { useDispatch } from "react-redux";
import {
  Language,
  updateAuthContext,
  updateCustomerInformationForSupportTicket,
  updateLanguage,
  updateTimezone,
} from "../reducers/your-reducer";
import { getSingleUserUseCase } from "@src/cloud-connect/user-manager/use-cases/get-single-user-use-case";
import { getAuthContext } from "./use-auth-context";
import { useTokenExpiryCheck } from "@src/hooks/use-token-expiry-check";
import {
  readAuthServiceEnvVar,
  readAuthAuthorityEnvVar,
  readAuthClientIdEnvVar,
} from "@src/cloud-connect/feature-toggles/feature-toggles";

const AUTH_SERVICE_ZITADEL = "zitadel";
const ZITADEL_SCOPES =
  "openid profile email urn:zitadel:iam:user:metadata urn:zitadel:iam:user:resourceowner urn:zitadel:iam:org:project:id:zitadel:aud";

export function enableOidcLogging(): void {
  OidcLog.setLogger(console);
}

/**
 * Handles the callback after successful sign-in
 * Removes auth payload from URL to prevent issues with token renewal
 */
const handleSigninCallback = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname);
};

const createBaseOidcConfig = (): AuthProviderProps => ({
  authority: readAuthAuthorityEnvVar(),
  client_id: readAuthClientIdEnvVar(),
  redirect_uri: `${window.location.origin}/login`,
  onSigninCallback: handleSigninCallback,
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  loadUserInfo: true,
  // tokens are valid 24h and we want exactly one first login:
  // no background renewal, the token-expiry check handles the end of the session
  automaticSilentRenew: false,
});

/**
 * Adds auth service-specific scopes to the OIDC config
 */
const addAuthServiceScopes = (config: AuthProviderProps): AuthProviderProps => {
  const authService = readAuthServiceEnvVar();

  if (authService === AUTH_SERVICE_ZITADEL) {
    return {
      ...config,
      scope: ZITADEL_SCOPES,
    };
  }

  return config;
};

const SUPPORTED_LANGUAGES = [
  "en",
  "de",
  "zh",
  "it",
  "es",
  "pt",
  "nl",
  "tr",
] as const;

const isValidLanguage = (lang: string): lang is Language => {
  return SUPPORTED_LANGUAGES.includes(lang as Language);
};

/**
 * Component that tracks authentication state changes and updates Redux store
 */
const AuthTracker = ({ children }: PropsWithChildren) => {
  const auth = useAuth();
  const dispatch = useDispatch();

  const updateAuthState = useCallback(
    async (user: User) => {
      const userId = user.profile.sub;
      const context = await getAuthContext(user);

      // prevent cross-user updates: discard stale async results from previous user sessions
      if (auth.user && auth.user?.profile.sub !== userId) return;

      const savedLanguage = sessionStorage.getItem("user-language") as Language;

      if (!context) {
        console.error("Failed to get auth context for user", userId);
        return;
      }

      // only use token language if sessionStorage is empty (first login)
      if (!savedLanguage) {
        const tokenLanguage = user.profile?.locale as string;
        const language = isValidLanguage(tokenLanguage) ? tokenLanguage : "en";
        sessionStorage.setItem("user-language", language);
        dispatch(updateLanguage(language));
      } else {
        const language = isValidLanguage(savedLanguage) ? savedLanguage : "en";
        dispatch(updateLanguage(language));
      }

      dispatch(updateAuthContext(context));
      dispatch(
        updateCustomerInformationForSupportTicket(
          context.userInfoForSupportTicket,
        ),
      );

      // update timezone in store -> non-blocking
      getSingleUserUseCase(context.userId).then((userResult) => {
        if (userResult.success && userResult.data?.chosen_timezone) {
          dispatch(updateTimezone(userResult.data.chosen_timezone));
        }
      });
    },
    [auth.user, dispatch],
  );

  useEffect(() => {
    const savedLanguage = sessionStorage.getItem("user-language") as Language;
    if (savedLanguage) {
      dispatch(updateLanguage(savedLanguage));
    }
  }, [dispatch]);

  // Handle auth state
  useEffect(() => {
    if (auth.user) {
      updateAuthState(auth.user);
    }

    // subscribe to user loaded events for token renewals
    const unsubscribe = auth.events.addUserLoaded((user: User) => {
      updateAuthState(user);
    });

    return unsubscribe;
  }, [auth.user, auth.events, updateAuthState]);

  // centralized token expiry check (see use-token-expiry-check.ts)
  useTokenExpiryCheck();

  return <>{children}</>;
};

/**
 * Root authentication provider component
 * Wraps the application with OIDC authentication and state tracking
 */
export const AuthProvider = ({ children }: PropsWithChildren) => {
  const oidcConfig = addAuthServiceScopes(createBaseOidcConfig());

  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthTracker>{children}</AuthTracker>
    </OidcAuthProvider>
  );
};
